import { produce } from 'immer';
import type { UnknownAction } from 'redux';

import type { Icon } from '../../../../../../../common/icons/iconTypes';
import type { IconPickerState } from './iconPickerTypes';
import {
    ICON_PICKER_RECENT_SEARCH_ADD,
    ICON_PICKER_SEARCH_ERROR,
    ICON_PICKER_SEARCH_LOADING,
    ICON_PICKER_SEARCH_SUCCESS,
} from './iconPickerConstants';

export type IconPickerAction =
    | { type: typeof ICON_PICKER_SEARCH_LOADING; query: string }
    | { type: typeof ICON_PICKER_SEARCH_SUCCESS; query: string; icons: Icon[] }
    | { type: typeof ICON_PICKER_SEARCH_ERROR; query: string }
    | { type: typeof ICON_PICKER_RECENT_SEARCH_ADD; elementId: string; query: string };

const initialState: IconPickerState = {
    // No prototype, so a search for "constructor" or "__proto__" isn't mistaken for a cached one.
    searches: Object.create(null),
    recentSearches: {},
};

/**
 * Session-only icon search results and recent searches for the Roma icon picker.
 * Mounted under `app.popup`, which hydration skips, so nothing survives a reload.
 */
const iconPickerReducer = (state = initialState, action: IconPickerAction | UnknownAction): IconPickerState => {
    const iconPickerAction = action as IconPickerAction;

    switch (iconPickerAction.type) {
        case ICON_PICKER_SEARCH_LOADING:
            return produce(state, (draft) => {
                draft.searches[iconPickerAction.query] = { status: 'loading', icons: [] };
            });

        case ICON_PICKER_SEARCH_SUCCESS:
            return produce(state, (draft) => {
                draft.searches[iconPickerAction.query] = { status: 'success', icons: iconPickerAction.icons };
            });

        case ICON_PICKER_SEARCH_ERROR:
            return produce(state, (draft) => {
                draft.searches[iconPickerAction.query] = { status: 'error', icons: [] };
            });

        case ICON_PICKER_RECENT_SEARCH_ADD:
            return produce(state, (draft) => {
                const { elementId, query } = iconPickerAction;
                const otherSearches = (draft.recentSearches[elementId] ?? []).filter(
                    (recentSearch) => recentSearch !== query,
                );

                // Repeating a search moves it to the end, so the list stays oldest first.
                draft.recentSearches[elementId] = [...otherSearches, query];
            });

        default:
            return state;
    }
};

export default iconPickerReducer;
